import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import type { Product } from '../../../../types/tycoon';
import { ProductDetailModal } from '../modals/ProductDetailModal';
import { ProductCard } from '../../../products/ProductCard';
import { useAllProducts } from '../../../../stores/marketStore';

interface ProductPortfolioSectionProps {
  products: Product[];
  onProductAction: (productId: string, action: string) => void;
  onNavigate?: () => void;
  companyName?: string;
  gameDate?: { day: number; month: number; year: number };
  getPhaseProgress?: (product: Product, currentDate: { day: number; month: number; year: number }) => { progress: number; daysRemaining: number; totalDays: number };
}

export function ProductPortfolioSection({ products, onProductAction, onNavigate, companyName, gameDate, getPhaseProgress }: ProductPortfolioSectionProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // Get all products (player + competitor) for accurate market share calculation
  const { allProducts, loading } = useAllProducts();

  // Calculate market share fresh from units sold (same approach as ProductListingTable)
  // This ensures market share is always accurate and not dependent on stale stored values
  const productsWithMarketShare = useMemo(() => {
    // Don't calculate market share until competitor products are loaded
    // This prevents showing 100% market share during the initial async load
    if (loading) {
      return products.map(p => ({
        ...p,
        salesMetrics: {
          ...p.salesMetrics,
          marketShare: 0
        }
      }));
    }

    // Calculate total units sold across ALL active products (player + competitors)
    const totalUnits = allProducts
      .filter(p => p.unitsSold > 0 && p.status !== 'discontinued')
      .reduce((sum, p) => sum + p.unitsSold, 0);

    // Enhance player products with calculated market share
    return products.map(p => ({
      ...p,
      salesMetrics: {
        ...p.salesMetrics,
        // Market share as decimal (0-1) for ProductCard display
        marketShare: totalUnits > 0
          ? (p.salesMetrics?.unitsSold || 0) / totalUnits
          : 0
      }
    }));
  }, [products, allProducts, loading]);

  // Show actual products, or empty state if none
  const displayProducts = productsWithMarketShare.length > 0 ? productsWithMarketShare : [];
  const productsPerPage = 3;
  const totalPages = Math.ceil(displayProducts.length / productsPerPage);
  const hasProducts = products.length > 0;

  // Get products for current page
  const getCurrentPageProducts = () => {
    const startIndex = currentPage * productsPerPage;
    return displayProducts.slice(startIndex, startIndex + productsPerPage);
  };

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const handleAction = (product: Product) => {
    // Always show the product popup for any product click
    if (hasProducts) {
      setSelectedProduct(product);
    }
  };

  return (
    <div className="border border-theme-border bg-theme-bg-content/50 rounded-lg relative overflow-hidden h-full" data-tutorial="product-portfolio">
      <div className="absolute top-0 left-0 right-0 z-10 bg-theme-bg-content p-2 border-b border-theme-border flex items-center justify-between">
        <button
          onClick={onNavigate}
          className={`font-bold text-sm text-theme-text-secondary ${
            onNavigate ? 'hover:text-theme-info transition-colors cursor-pointer' : ''
          }`}
          disabled={!onNavigate}
        >
          Product Portfolio
        </button>
        <button
          onClick={() => onProductAction('new', 'develop')}
          className="flex items-center gap-1 px-2 py-1 bg-theme-accent text-white text-xs rounded hover:bg-theme-accent-hover transition-colors"
          title="Create New Product"
          data-testid="create-product-button"
          data-tutorial="new-product-button"
        >
          <Plus className="w-3 h-3" />
          New
        </button>
      </div>
      <div className="pt-10 p-3 h-full flex flex-col min-h-0">
        <div className="bg-theme-bg-tertiary/50 rounded-lg border border-theme-border flex-1 mx-2 my-2 min-h-0 flex flex-col">
          {/* Navigation Header */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-2">
              <button 
                onClick={prevPage}
                className="p-1 hover:bg-theme-bg-tertiary rounded transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-theme-text-secondary" />
              </button>
              <span className="text-xs text-theme-text-secondary">
                Page {currentPage + 1} of {totalPages}
              </span>
              <button 
                onClick={nextPage}
                className="p-1 hover:bg-theme-bg-tertiary rounded transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-theme-text-secondary" />
              </button>
            </div>
          )}
          
          {/* Products Grid */}
          <div className="flex-1 p-2 overflow-hidden">
            {hasProducts ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-2">
                {getCurrentPageProducts().map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onSelect={() => handleAction(product)}
                  />
                ))}

                {/* Fill empty slots if less than 3 products on current page */}
                {Array.from({ length: productsPerPage - getCurrentPageProducts().length }).map((_, index) => (
                  <div key={`empty-${index}`} className="flex flex-col h-full">
                    <div className="flex-1 border border-theme-border rounded-lg flex items-center justify-center min-h-[120px]">
                      <div className="w-6 h-6 bg-theme-bg-tertiary rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Empty State */
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-theme-text-muted">
                  <div className="flex-1 border border-theme-border rounded-lg flex items-center justify-center mb-3 mx-auto min-h-[120px] max-w-[200px]">
                    <Plus className="w-12 h-12 text-theme-text-muted" />
                  </div>
                  <div className="text-sm">No products yet</div>
                  <div className="text-sm">Click "New" to create</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Page Indicator */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-1 mt-2">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentPage ? 'bg-theme-accent' : 'bg-theme-bg-tertiary'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Product Detail Modal */}
      <ProductDetailModal
        isOpen={selectedProduct !== null}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct}
        onAction={onProductAction}
        isPlayerProduct={true}
        companyName={companyName || 'Your Company'}
        gameDate={gameDate}
        getPhaseProgress={getPhaseProgress}
      />
    </div>
  );
}